/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  forwardRef, useEffect, useMemo, useCallback,
} from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import {
  setRoomValidationResult,
} from '@redux/modules/formGeneration';

import { selectRoomStatusById, selectComboBoxOptions } from '@redux/selectors/FormSelector';

import Card from '@components/smartAdminUI/Card';
import Button from '@components/smartAdminUI/Button';
import styles from '@components/form/formComponents/repeatableRoom/RoomCard.module.css';
import RoomMeasurement from '@components/form/formComponents/repeatableRoom/RoomMeasurement';

const roomRules = window.FlexRule.RuntimeEngine.fromJavaScript('OnePlatform', 'RoomRules');

const RoomCard = forwardRef(
  ({
    attributes,
    item,
    className,
    color,
    dragOverlay,
    dragging,
    disabled,
    fadeIn,
    handle,
    height,
    listeners,
    renderItem,
    sorting,
    style,
    transition,
    transform,
    value,
    wrapperStyle,
    index,
    onRemoveRoom,
    role,
    formik,
    id,
    ...props
  }, ref) => {
    const dispatch = useDispatch();
    const roomLvlOptions = useSelector(state => selectComboBoxOptions(state, 'RoomLevel'));
    const roomTypeOptions = useSelector(state => selectComboBoxOptions(state, 'RoomType'));
    const values = formik?.values?.Rooms[id];
    const roomLvl = useMemo(() => roomLvlOptions.find(opt => opt.value === values.RoomLevel), [values, roomLvlOptions]);
    const roomType = useMemo(() => roomTypeOptions.find(opt => opt.value === values.RoomType), [values, roomTypeOptions]);

    const roomStatus = useSelector(state => selectRoomStatusById(state, id));
    // validates total room, then sets the results in redux
    const validateRoomField = () => {
      if (!values.IsDeleted) {
        const res = roomRules.run(values);
        const { fieldValidationResult } = res.context.variableContainer;
        dispatch(setRoomValidationResult(id, fieldValidationResult));
      }
    };

    // sets field as touched, then triggers validation on field
    const handleBlur = useCallback(fieldName => () => {
      formik.setFieldTouched(fieldName, true);
      validateRoomField();
    }, [values]);

    const lvlOnChange = opt => {
      formik.setFieldValue(`Rooms.[${id}].RoomLevel`, opt?.value);
    };

    const typeOnChange = opt => {
      formik.setFieldValue(`Rooms.[${id}].RoomType`, opt?.value);
    };

    useEffect(() => {
      if (!dragOverlay) {
        return undefined;
      }

      document.body.style.cursor = 'grabbing';

      return () => {
        document.body.style.cursor = '';
      };
    }, [dragOverlay]);

    const removeRoom = () => {
      onRemoveRoom(id);
    };
    return (renderItem && item) ? (
      renderItem({
        key: id,
        dragOverlay: Boolean(dragOverlay),
        dragging: Boolean(dragging),
        sorting: Boolean(sorting),
        index,
        fadeIn: Boolean(fadeIn),
        listeners,
        onRemoveRoom,
        ref,
        style,
        transform,
        transition,
        value,
      })
    ) : (
      <>
        <li
          key={id}
          ref={ref}
          className={classNames(
            className,
            styles.Wrapper,
            fadeIn && styles.fadeIn,
            sorting && styles.sorting,
          )}
        >
          <div
            className={classNames(
              styles.Item,
              dragging && styles.dragging,
              handle && styles.withHandle,
              dragOverlay && styles.dragOverlay,
              disabled && styles.disabled,
              color && styles.color,
            )}
            style={style}
            {...attributes}
            {...listeners}
            {...props}
            tabIndex={!handle ? 0 : undefined}
          >
            <Card
              header={(
                <>
                  <span className="text-muted">{values.SortOrder}</span>
                  <Button
                    name="btnDeleteRoom"
                    variant="primary btn-sm float-right"
                    onClick={removeRoom}
                    tooltipText="Delete Room"
                  >
                    <i className="far fa-trash-alt" aria-hidden="true" />
                  </Button>
                </>
              )}
            >
              <RoomMeasurement id={id} formik={formik} handleBlur={handleBlur} />
              <div className="form-group" id={`Rooms.[${id}].RoomType`}>
                <label
                  className="form-label"
                  htmlFor={`Rooms.[${id}].RoomType`}
                >
                  Type
                </label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={disabled}
                  isClearable
                  onBlur={handleBlur(`Rooms.[${id}].RoomType`)} // surfaces errors when control click, but no select
                  onChange={typeOnChange} // used to select and format values for validation/flexRule
                  options={roomTypeOptions}
                  value={roomType}
                  menuPortalTarget={document.querySelector('body')}
                  placeholder="Choose one"
                />
                {roomStatus?.RoomType?.errorMessage && (
                  <div className="invalid-feedback">{roomStatus.RoomType.errorMessage}</div>)}
              </div>
              <div className="form-group" id={`Rooms.[${id}].RoomLevel`}>
                <label
                  className="form-label"
                  htmlFor={`Rooms.[${id}].RoomLevel`}
                >
                  Level
                </label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={disabled}
                  isClearable
                  onBlur={handleBlur(`Rooms.[${id}].RoomLevel`)} // surfaces errors when control click, but no select
                  onChange={lvlOnChange} // used to select and format values for validation/flexRule
                  options={roomLvlOptions}
                  value={roomLvl}
                  menuPortalTarget={document.querySelector('body')}
                  placeholder="Choose one"
                />
                {roomStatus?.RoomLevel?.errorMessage && (
                  <div className="invalid-feedback">{roomStatus.RoomLevel.errorMessage}</div>)}
              </div>
            </Card>
          </div>
        </li>
      </>
    );
  },
);

RoomCard.propTypes = {
  attributes: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  dragOverlay: PropTypes.bool,
  dragging: PropTypes.bool,
  disabled: PropTypes.bool,
  fadeIn: PropTypes.bool,
  handle: PropTypes.bool,
  height: PropTypes.string,
  key: PropTypes.string,
  listeners: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  id: PropTypes.string.isRequired,
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  item: PropTypes.arrayOf(PropTypes.string),
  renderItem: PropTypes.bool,
  sorting: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  transform: PropTypes.bool,
  transition: PropTypes.string,
  value: PropTypes.string,
  wrapperStyle: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  onRemoveRoom: PropTypes.func,
  role: PropTypes.string,
  formik: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.shape(), PropTypes.number, PropTypes.func])),
};

export default RoomCard;
